import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useLoad } from '../hooks';
import { PageHead } from '../Layout';
import { Dialog } from '../components/Dialog';
import { Disclosure } from '../components/Disclosure';
import { Icon } from '../components/Icon';
import { formatDateTime, formatDuration, formatRelative } from '../format';
import type { AgentInfo } from '../../shared/types';
import { MAX_TREND_CANDIDATE_POSTS, TREND_MATERIAL_DEFAULTS } from '../../shared/types';

const INSTRUCTED_SLUGS = new Set(['editor', 'curator']);

interface TunableField {
  key: string;
  label: string;
  /** the code default, shown as the input placeholder when no override is set */
  fallback: number;
  hint: string;
  step?: number;
}

/** Numeric per-agent settings editable from the gear modal, by slug. */
const AGENT_TUNABLES: Record<string, TunableField[]> = {
  trends: [
    { key: 'maxTrendResearchPerRun', label: 'Trends researched / run', fallback: 3, hint: `Fresh trends researched per run (each costs a web-search call plus up to ${MAX_TREND_CANDIDATE_POSTS} post reads).` },
    { key: 'minTrendMaterialScore', label: 'Minimum material relevance', fallback: TREND_MATERIAL_DEFAULTS.minScore, step: 0.05, hint: 'Reranker score required before a passage counts toward trend qualification. Default 0.7; this is a relevance score, not a probability.' },
    { key: 'minTrendMaterialWords', label: 'Minimum strong material (words)', fallback: TREND_MATERIAL_DEFAULTS.minWords, hint: 'Strongly matched member words required before paid research. Counts only one chunk per source and excludes duplicate passages.' },
  ],
};

type HermesRole = 'observer' | 'contributor' | 'editor';

interface HermesIntegration {
  slug: string;
  displayName: string;
  role: HermesRole;
  enabled: boolean;
  configured: boolean;
  lastUsedAt: string | null;
  configurationVersion: number;
}

interface HermesEvent {
  id: string | number;
  toolName: string;
  status: string;
  resourceType: string | null;
  resourceId: string | number | null;
  durationMs: number | null;
  createdAt: string;
}

interface HermesResponse {
  integration: HermesIntegration;
  events: HermesEvent[];
}

const HERMES_ROLES: Array<{ value: HermesRole; label: string }> = [
  { value: 'observer', label: 'Observer' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'editor', label: 'Editor' },
];

/** Gear-icon modal for numeric agent tunables. Empty field = use the default. */
function TunablesModal({ slug, onClose }: { slug: string; onClose: () => void }) {
  const fields = AGENT_TUNABLES[slug] ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ agentSettings?: Record<string, Record<string, unknown>> }>('/settings')
      .then((settings) => {
        if (cancelled) return;
        const stored = settings.agentSettings?.[slug] ?? {};
        setValues(
          Object.fromEntries(
            fields.map((f) => {
              const v = stored[f.key];
              return [f.key, typeof v === 'number' ? String(v) : ''];
            }),
          ),
        );
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const patch: Record<string, number | null> = {};
      for (const f of fields) {
        const raw = (values[f.key] ?? '').trim();
        if (raw === '') {
          patch[f.key] = null; // clear the override — the agent uses its default
          continue;
        }
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) {
          setError(`${f.label} must be a positive number (or empty for the default).`);
          setSaving(false);
          return;
        }
        patch[f.key] = n;
      }
      await api.patch(`/agents/${slug}`, patch);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Settings"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={() => void save()} disabled={loading || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
        {loading ? (
          <p className="faint">Loading…</p>
        ) : (
          <div className="tunables-grid">
            {fields.map((f) => (
              <label key={f.key} title={f.hint}>
                <span className="label">{f.label}</span>
                <input
                  type="number"
                  min={0}
                  step={f.step ?? 1}
                  placeholder={String(f.fallback)}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  disabled={saving}
                />
              </label>
            ))}
          </div>
        )}
        {error ? <p className="fail">{error}</p> : null}
    </Dialog>
  );
}

/** Gear-icon modal: view/edit an agent's custom-instructions bullet text. */
function CustomInstructionsModal({ slug, onClose }: { slug: string; onClose: () => void }) {
  const [value, setValue] = useState('');
  // The last value we know matches the server, sent back as
  // expectedCustomInstructions so a concurrent archivist/chronicler write
  // can be detected instead of silently overwritten.
  const [expected, setExpected] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rationalizing, setRationalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set only on a 409: the user's unsaved draft, preserved read-only so a
  // stale save never silently loses either side.
  const [conflictDraft, setConflictDraft] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ agentSettings?: Record<string, { customInstructions?: string }> }>('/settings')
      .then((settings) => {
        if (cancelled) return;
        const loaded = settings.agentSettings?.[slug]?.customInstructions ?? '';
        setValue(loaded);
        setExpected(loaded);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/agents/${slug}`, {
        customInstructions: value,
        expectedCustomInstructions: expected,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const current =
          (err.data as { current?: string } | undefined)?.current ?? value;
        setConflictDraft(value);
        setValue(current);
        setExpected(current);
        setError(
          'Updated by the system while you edited — review the latest below and re-apply your changes.',
        );
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSaving(false);
    }
  }

  /** Dedupes/reconciles the instructions server-side, then loads the result back in. */
  async function rationalize() {
    setRationalizing(true);
    setError(null);
    try {
      const result = await api.post<{ changed: boolean; after: string }>(
        `/agents/${slug}/rationalize-instructions`,
      );
      setValue(result.after);
      setExpected(result.after);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRationalizing(false);
    }
  }

  return (
    <Dialog
      title="Custom instructions"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button
            onClick={() => void rationalize()}
            disabled={loading || saving || rationalizing}
          >
            {rationalizing ? 'Rationalizing…' : 'Rationalize'}
          </button>
          <button className="primary" onClick={() => void save()} disabled={loading || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
        {loading ? (
          <p className="faint">Loading…</p>
        ) : (
          <textarea
            rows={12}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="- one instruction per line"
            disabled={saving}
          />
        )}
        {error ? <p className="fail">{error}</p> : null}
        {conflictDraft !== null ? (
          <div className="conflict-draft">
            <span className="label">Your unsaved draft</span>
            <textarea rows={8} value={conflictDraft} readOnly />
          </div>
        ) : null}
    </Dialog>
  );
}

function HermesIntegrationCard() {
  const [data, setData] = useState<HermesResponse | null>(null);
  const [busy, setBusy] = useState<'settings' | 'rotate' | 'revoke' | null>(null);
  const [credential, setCredential] = useState<{ token: string; prefix: string } | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const { loading, error, setError, load } = useLoad(
    useCallback(async () => {
      setData(await api.get<HermesResponse>('/integrations/hermes'));
    }, []),
    { pollMs: 10_000 },
  );

  async function update(patch: Partial<Pick<HermesIntegration, 'role' | 'enabled'>>) {
    setBusy('settings');
    setError(null);
    try {
      await api.patch('/integrations/hermes', patch);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function rotateCredential() {
    if (
      data?.integration.configured &&
      !window.confirm('Rotate the Hermes credential? The current credential will stop working immediately.')
    ) {
      return;
    }
    setBusy('rotate');
    setError(null);
    setCredential(null);
    setCopyState('idle');
    try {
      const next = await api.post<{ token: string; prefix: string }>(
        '/integrations/hermes/rotate-token',
      );
      setCredential(next);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function revokeCredential() {
    if (!window.confirm('Revoke the Hermes credential? Hermes will lose access immediately.')) return;
    setBusy('revoke');
    setError(null);
    setCredential(null);
    setCopyState('idle');
    try {
      await api.post('/integrations/hermes/revoke-token');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function copyCredential() {
    if (!credential) return;
    try {
      await navigator.clipboard.writeText(credential.token);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }

  if (loading && !data) {
    return (
      <section className="integration-section" aria-labelledby="external-integrations-title">
        <h2 id="external-integrations-title">External integration</h2>
        <div className="empty">Loading Hermes…</div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="integration-section" aria-labelledby="external-integrations-title">
        <h2 id="external-integrations-title">External integration</h2>
        <p className="fail">{error ?? 'Hermes integration is unavailable.'}</p>
      </section>
    );
  }

  const { integration, events } = data;
  const status = !integration.configured
    ? 'Needs credential'
    : !integration.enabled
      ? 'Disabled'
      : 'Enabled';
  const statusOn = integration.configured && integration.enabled;

  return (
    <section className="integration-section" aria-labelledby="external-integrations-title">
      <div className="integration-section-head">
        <h2 id="external-integrations-title">External integration</h2>
        <span className="faint">Runs outside Tentacle</span>
      </div>
      <div className="card integration-card">
        <div className="card-head">
          <div>
            <h2>{integration.displayName}</h2>
            <p>Telegram’s conversational agent, with scoped access to Tentacle tools.</p>
          </div>
          <div className="integration-badges" aria-label={`Integration status: ${status}`}>
            <span className="chip">External</span>
            <span className={`chip${statusOn ? ' on' : ''}`}>{status}</span>
          </div>
        </div>

        <div className="integration-overview">
          <div>
            <span className="label">Permission</span>
            <strong>{HERMES_ROLES.find((role) => role.value === integration.role)?.label}</strong>
          </div>
          <div>
            <span className="label">Last used</span>
            <span title={formatDateTime(integration.lastUsedAt)}>
              {formatRelative(integration.lastUsedAt)}
            </span>
          </div>
          <div>
            <span className="label">Credential</span>
            <span>{integration.configured ? 'Configured' : 'Not configured'}</span>
          </div>
          <div>
            <span className="label">Configuration</span>
            <span>Version {integration.configurationVersion}</span>
          </div>
        </div>

        <div className="integration-controls">
          <label className="integration-role">
            <span className="label">Permission level</span>
            <select
              value={integration.role}
              onChange={(event) => void update({ role: event.target.value as HermesRole })}
              disabled={busy !== null}
            >
              {HERMES_ROLES.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </label>
          <label className="integration-enable">
            <input
              type="checkbox"
              checked={integration.enabled}
              onChange={(event) => void update({ enabled: event.target.checked })}
              disabled={busy !== null || !integration.configured}
            />
            <span>Allow Hermes to use Tentacle tools</span>
          </label>
          <div className="integration-credential-actions">
            <button onClick={() => void rotateCredential()} disabled={busy !== null}>
              {busy === 'rotate'
                ? 'Creating…'
                : integration.configured
                  ? 'Rotate credential'
                  : 'Create credential'}
            </button>
            {integration.configured ? (
              <button onClick={() => void revokeCredential()} disabled={busy !== null}>
                {busy === 'revoke' ? 'Revoking…' : 'Revoke credential'}
              </button>
            ) : null}
          </div>
        </div>

        {credential ? (
          <div className="integration-token" role="status">
            <div>
              <strong>Copy this credential now.</strong>
              <span>It will not be shown again after you close this panel.</span>
            </div>
            <code>{credential.token}</code>
            <div className="integration-token-actions">
              <button className="primary" onClick={() => void copyCredential()}>
                {copyState === 'copied' ? 'Copied' : 'Copy credential'}
              </button>
              <button onClick={() => setCredential(null)}>Close</button>
              {copyState === 'failed' ? (
                <span className="fail">Copy failed. Select the credential and copy it manually.</span>
              ) : null}
            </div>
            <span className="faint">Credential prefix: <span className="mono">{credential.prefix}</span></span>
          </div>
        ) : null}

        {error ? <p className="fail" role="status">{error}</p> : null}

        <Disclosure
          className="integration-activity"
          title="Recent activity"
          meta={events.length === 0 ? 'No tool calls yet' : `${events.length} recent tool ${events.length === 1 ? 'call' : 'calls'}`}
        >
          {events.length === 0 ? (
            <p className="faint">Hermes activity will appear here after its first Tentacle tool call.</p>
          ) : (
            <div className="table-scroll">
              <table className="integration-events">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th>Status</th>
                    <th>Resource</th>
                    <th>Duration</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const failed = ['failed', 'denied', 'error'].includes(event.status.toLowerCase());
                    const resource = event.resourceType
                      ? `${event.resourceType}${event.resourceId === null ? '' : ` ${event.resourceId}`}`
                      : '—';
                    return (
                      <tr key={event.id}>
                        <td className="mono">{event.toolName}</td>
                        <td><span className={failed ? 'fail' : undefined}>{event.status}</span></td>
                        <td>{resource}</td>
                        <td>{formatDuration(event.durationMs)}</td>
                        <td title={formatDateTime(event.createdAt)}>{formatRelative(event.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Disclosure>
      </div>
    </section>
  );
}

export default function Agents() {
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [instructionsSlug, setInstructionsSlug] = useState<string | null>(null);

  const { loading, error, setError, load } = useLoad(
    useCallback(async () => {
      const res = await api.get<{ items: AgentInfo[]; timezone: string }>('/agents');
      setAgents(res.items);
      setTimezone(res.timezone);
    }, []),
    { pollMs: 10_000 },
  );

  async function run(slug: string) {
    setBusy(slug);
    setError(null);
    try {
      await api.post(`/agents/${slug}/run`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
      await load();
    }
  }

  async function toggle(agent: AgentInfo) {
    setBusy(agent.slug);
    try {
      await api.patch(`/agents/${agent.slug}`, { enabled: !agent.enabled });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
      await load();
    }
  }

  function agentCard(agent: AgentInfo, operational: boolean) {
    const hasSettings = operational && (INSTRUCTED_SLUGS.has(agent.slug) || Boolean(AGENT_TUNABLES[agent.slug]));
    return (
      <article className={`card agent-card${operational ? ' operational' : ''}`} key={agent.slug}>
        <div className="card-head">
          <div>
            <h3>{agent.name}</h3>
            <span className="agent-slug">{agent.slug}</span>
          </div>
          <div className="agent-card-actions">
            {hasSettings ? (
              <button
                className="icon-btn"
                type="button"
                title={INSTRUCTED_SLUGS.has(agent.slug) ? 'Custom instructions' : 'Agent settings'}
                aria-label={INSTRUCTED_SLUGS.has(agent.slug) ? `Edit ${agent.name} custom instructions` : `Edit ${agent.name} settings`}
                onClick={() => setInstructionsSlug(agent.slug)}
              >
                <Icon name="settings" />
              </button>
            ) : null}
            <span className={`chip${agent.running ? ' on' : ''}`}>
              {agent.running ? 'running' : agent.kind}
            </span>
          </div>
        </div>
        <p>{agent.description}</p>
        <dl className="agent-facts">
          <div><dt>Schedule</dt><dd>{agent.schedule ?? 'On demand'}</dd></div>
          <div>
            <dt>Last run</dt>
            <dd>
              {agent.lastRun ? (
                <Link to={`/activity/${agent.lastRun.id}`}>
                  <span className={agent.lastRun.status === 'failed' ? 'fail' : undefined}>{agent.lastRun.status}</span>{' '}
                  <span className="faint" title={formatDateTime(agent.lastRun.startedAt)}>{formatRelative(agent.lastRun.startedAt)}</span>
                </Link>
              ) : 'No run recorded'}
            </dd>
          </div>
          <div><dt>Next run</dt><dd>{agent.nextRun && new Date(agent.nextRun).getTime() <= Date.now() ? 'Due now' : (formatDateTime(agent.nextRun) || '—')}</dd></div>
        </dl>
        {operational ? (
          <div className="card-foot">
            <button onClick={() => void toggle(agent)} disabled={busy === agent.slug}>
              {agent.enabled ? 'Disable' : 'Enable'}
            </button>
            <button className="primary" onClick={() => void run(agent.slug)} disabled={busy === agent.slug || agent.running}>
              {busy === agent.slug ? 'Running…' : 'Run now'}
            </button>
          </div>
        ) : null}
      </article>
    );
  }

  const archivist = agents.find((agent) => agent.slug === 'archivist') ?? null;
  const legacyAgents = agents.filter((agent) => agent.slug !== 'archivist');

  return (
    <div className="agents-page">
      <PageHead
        title="Agents"
        subtitle="The active systems that preserve material and carry editorial work forward."
      />
      {error ? <p className="fail">{error}</p> : null}
      {loading ? (
        <div className="empty">Loading…</div>
      ) : (
        <>
          <HermesIntegrationCard />

          <section className="active-agent-section" aria-labelledby="active-system-title">
            <div className="section-heading">
              <div><h2 id="active-system-title">Archive intake</h2><p>Preserves and prepares incoming material for the Library.</p></div>
              {timezone ? <span>Times in {timezone}</span> : null}
            </div>
            {archivist
              ? agentCard(archivist, true)
              : error
                ? <div className="empty">The agent registry is unavailable.</div>
                : <div className="empty">Archivist is not present in the current registry.</div>}
          </section>

          {agents.length > 0 ? (
            <Disclosure
              className="legacy-agents"
              title="Legacy agent history"
              meta={`${legacyAgents.length} retired workflow component${legacyAgents.length === 1 ? '' : 's'}`}
            >
              <p className="legacy-note">
                These records belong to the earlier six-agent workflow. Current editorial work is managed in <Link to="/dashboard">Publications</Link>.
              </p>
              <div className="legacy-agent-grid">{legacyAgents.map((agent) => agentCard(agent, false))}</div>
            </Disclosure>
          ) : null}
        </>
      )}
      {instructionsSlug ? (
        INSTRUCTED_SLUGS.has(instructionsSlug) ? (
          <CustomInstructionsModal slug={instructionsSlug} onClose={() => setInstructionsSlug(null)} />
        ) : (
          <TunablesModal slug={instructionsSlug} onClose={() => setInstructionsSlug(null)} />
        )
      ) : null}
    </div>
  );
}
